/**
 * Thin fetch wrapper for the optified API.
 *
 * Two things it takes care of that every call needs:
 *  - `credentials: 'include'`, because auth is an HttpOnly session cookie rather than a bearer
 *    token in localStorage (nothing for XSS to steal).
 *  - the `X-Tenant-Slug` header, which is how the workspace is selected when the app is not
 *    running on a per-tenant subdomain — which is always the case behind the Vite dev proxy.
 */

const TENANT_STORAGE_KEY = 'optified.tenant'

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly title: string,
    readonly detail: string | null,
    readonly errors: Record<string, string[]> = {},
  ) {
    super(detail || title)
    this.name = 'ApiError'
  }

  /** Flattened field errors, ready for a form summary. */
  get fieldMessages(): string[] {
    return Object.values(this.errors).flat()
  }

  get isUnauthorized(): boolean {
    return this.status === 401
  }

  /** 402 is used for plan/seat limits, which deserve an upgrade prompt rather than an error toast. */
  get isPlanLimit(): boolean {
    return this.status === 402
  }
}

export function getTenantSlug(): string | null {
  try {
    return localStorage.getItem(TENANT_STORAGE_KEY)
  } catch {
    return null
  }
}

export function setTenantSlug(slug: string | null): void {
  try {
    if (slug) localStorage.setItem(TENANT_STORAGE_KEY, slug)
    else localStorage.removeItem(TENANT_STORAGE_KEY)
  } catch {
    /* private browsing — the subdomain or the session cookie will have to carry the tenant */
  }
}

type QueryValue = string | number | boolean | null | undefined

interface RequestOptions {
  method?: string
  body?: unknown
  query?: Record<string, QueryValue>
  signal?: AbortSignal
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, query, signal } = options

  const url = new URL(path, window.location.origin)
  for (const [key, value] of Object.entries(query ?? {})) {
    if (value !== null && value !== undefined && value !== '') {
      url.searchParams.set(key, String(value))
    }
  }

  const headers: Record<string, string> = { Accept: 'application/json' }
  if (body !== undefined) headers['Content-Type'] = 'application/json'

  const tenant = getTenantSlug()
  if (tenant) headers['X-Tenant-Slug'] = tenant

  const response = await fetch(url, {
    method,
    headers,
    credentials: 'include',
    body: body === undefined ? undefined : JSON.stringify(body),
    signal,
  })

  if (response.status === 204) return undefined as T

  const payload = await readJson(response)

  if (!response.ok) {
    throw new ApiError(
      response.status,
      payload?.title ?? response.statusText ?? 'Request failed',
      payload?.detail ?? null,
      payload?.errors ?? {},
    )
  }

  return payload as T
}

async function readJson(response: Response): Promise<any> {
  const text = await response.text()
  if (!text) return null
  try {
    return JSON.parse(text)
  } catch {
    return { title: 'Unexpected response', detail: text.slice(0, 500) }
  }
}

/** Multipart. The browser sets Content-Type with the boundary; setting it ourselves would break it. */
async function upload<T>(path: string, form: FormData): Promise<T> {
  const headers: Record<string, string> = { Accept: 'application/json' }
  const tenant = getTenantSlug()
  if (tenant) headers['X-Tenant-Slug'] = tenant

  const response = await fetch(new URL(path, window.location.origin), {
    method: 'POST',
    headers,
    credentials: 'include',
    body: form,
  })

  const payload = await readJson(response)

  if (!response.ok) {
    throw new ApiError(
      response.status,
      payload?.title ?? response.statusText ?? 'Upload failed',
      payload?.detail ?? null,
      payload?.errors ?? {},
    )
  }

  return payload as T
}

export const api = {
  get: <T>(path: string, query?: Record<string, QueryValue>, signal?: AbortSignal) =>
    request<T>(path, { method: 'GET', query, signal }),
  upload,
  post: <T>(path: string, body?: unknown) => request<T>(path, { method: 'POST', body }),
  put: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PUT', body }),
  // A body on a DELETE is unusual and deliberate in the one place it is used: forgetting a push
  // subscription is addressed by its endpoint URL, which is far too long to put in a query string.
  del: <T = void>(path: string, body?: unknown) => request<T>(path, { method: 'DELETE', body }),
}
